import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';
import {
  TrendingUp, TrendingDown, Minus, Target, AlertTriangle, CheckCircle2,
  Activity, ClipboardList, FileDown, Flag, Dumbbell, Heart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import OnboardingBanner from '../components/OnboardingBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getRatingColor = (r) => {
  if (!r) return 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-500';
  if (r >= 7) return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
  if (r >= 5) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
  return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
};

const getLabelColor = (label) => {
  if (label === 'Excellent') return 'text-emerald-600';
  if (label === 'Good') return 'text-blue-600';
  if (label === 'Average') return 'text-amber-600';
  return 'text-red-500';
};

const ParentDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [athleteInfo, setAthleteInfo] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [parentSummary, setParentSummary] = useState(null);
  const [pdpData, setPdpData] = useState(null);
  const [injuryData, setInjuryData] = useState(null);
  const [careerData, setCareerData] = useState(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchParentData(); }, []);

  const fetchParentData = async () => {
    setLoading(true);
    try {
      const athletesRes = await axios.get(`${API}/athletes`, { headers });
      const athletes = Array.isArray(athletesRes.data) ? athletesRes.data : [];
      if (athletes.length === 0) { setLoading(false); return; }

      const athleteId = athletes[0].id;
      const [dashRes, summaryRes, pdpRes, injuryRes, careerRes] = await Promise.allSettled([
        axios.get(`${API}/athletes/${athleteId}/360`, { headers }),
        axios.get(`${API}/athletes/${athleteId}/parent-summary`, { headers }),
        axios.get(`${API}/pdp/cycles/athlete/${athleteId}`, { headers }),
        axios.get(`${API}/injury-recovery/status/athlete/${athleteId}`, { headers }),
        axios.get(`${API}/career-v2/phases/athlete/${athleteId}`, { headers }),
      ]);

      if (dashRes.status === 'fulfilled') {
        setAthleteInfo(dashRes.value.data.athlete);
        setSnapshot(dashRes.value.data.performance_snapshot);
      }
      if (summaryRes.status === 'fulfilled') setParentSummary(summaryRes.value.data);
      if (pdpRes.status === 'fulfilled') setPdpData(pdpRes.value.data);
      if (injuryRes.status === 'fulfilled') setInjuryData(injuryRes.value.data);
      if (careerRes.status === 'fulfilled') setCareerData(careerRes.value.data);
    } catch (error) {
      console.error('Failed to load parent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = () => {
    if (!parentSummary || !athleteInfo) return;
    const s = parentSummary.season_stats;
    const a = parentSummary.attendance;
    let csv = `Athlete Progress Report\n`;
    csv += `Name: ${athleteInfo.full_name}\n`;
    csv += `Position: ${athleteInfo.position || 'N/A'}\n`;
    csv += `Squad: ${athleteInfo.squad || 'N/A'}\n\n`;
    csv += `Season Performance\n`;
    csv += `Matches,${s.matches}\nGoals,${s.goals}\nAssists,${s.assists}\nMinutes,${s.minutes}\n`;
    csv += `Avg Rating,${s.avg_rating || 'N/A'}\nOverall,${s.avg_rating_label || 'N/A'}\n\n`;
    csv += `Attendance\nSessions,${a.total_sessions}\nAttended,${a.attended}\nRate,${a.rate}%\n\n`;
    csv += `Match History\nDate,Opponent,Rating,Label,Goals,Assists\n`;
    (parentSummary.performance_trend || []).forEach(m => {
      csv += `${m.match_date},${m.opponent},${m.rating || '-'},${m.rating_label || '-'},${m.goals},${m.assists}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(athleteInfo.full_name || 'athlete').replace(/\s+/g, '_')}_report.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pfap-green" />
        </div>
      </Layout>
    );
  }

  if (!athleteInfo) {
    return (
      <Layout>
        <div className="text-center py-16 text-gray-500 dark:text-gray-500" data-testid="no-athlete-msg">
          <p>No linked athlete found. Please contact academy administration.</p>
        </div>
      </Layout>
    );
  }

  const ss = parentSummary?.season_stats || {};
  const att = parentSummary?.attendance || {};
  const trend = parentSummary?.performance_trend || [];
  const flags = parentSummary?.active_flags || [];
  const activeCycle = pdpData?.active_cycle;
  const cycleGoals = pdpData?.goals || [];
  const pdpProgress = cycleGoals.length > 0
    ? Math.round(cycleGoals.reduce((sum, g) => sum + (g.progress || 0), 0) / cycleGoals.length)
    : 0;
  const maxRating = trend.length > 0 ? Math.max(...trend.map(t => t.rating || 0)) : 10;

  return (
    <Layout>
      <div className="space-y-5 max-w-6xl" data-testid="parent-dashboard">
        <OnboardingBanner
          id="parent-dashboard-v1"
          description="This is your child's overview: season stats, attendance, active alerts, development plan, and injury status — all in one place. Download a progress report anytime with the button on the right."
        />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-pfap-green/10 flex items-center justify-center">
              <span className="text-lg font-bold text-pfap-green">{(athleteInfo.full_name || '?')[0]}</span>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100" data-testid="athlete-name">{athleteInfo.full_name}</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {athleteInfo.position || 'Player'} &middot; {athleteInfo.squad || 'Academy'} — Parent View
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleDownloadReport} className="h-8" data-testid="download-report-btn">
            <FileDown size={14} className="mr-1.5" /> Download Report
          </Button>
        </div>

        {/* Key Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="key-stats">
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] uppercase text-gray-400 mb-1">Avg Rating</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{ss.avg_rating || '--'}</p>
              {ss.avg_rating_label && <p className={`text-[10px] font-medium ${getLabelColor(ss.avg_rating_label)}`}>{ss.avg_rating_label}</p>}
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] uppercase text-gray-400 mb-1">Matches</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{ss.matches || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] uppercase text-gray-400 mb-1">Goals</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{ss.goals || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] uppercase text-gray-400 mb-1">Assists</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{ss.assists || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] uppercase text-gray-400 mb-1">Minutes</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{ss.minutes || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <CardContent className="p-3 text-center">
              <p className="text-[10px] uppercase text-gray-400 mb-1">Attendance</p>
              <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{att.rate || 0}%</p>
            </CardContent>
          </Card>
        </div>

        {/* Performance Trend */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="performance-trend-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-700 dark:text-gray-300">
              <Activity size={15} className="inline mr-1.5 mb-0.5" /> Performance Trend
            </CardTitle>
            <CardDescription className="text-xs">Last {trend.length} matches — rating + qualitative assessment</CardDescription>
          </CardHeader>
          <CardContent>
            {trend.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8">No match data yet — match reports will appear here after your child plays.</p>
            ) : (
              <>
                {/* Bar chart */}
                <div className="flex items-end gap-1.5 h-32 mb-3" data-testid="trend-chart">
                  {trend.map((m, i) => {
                    const height = m.rating ? Math.max((m.rating / 10) * 100, 8) : 8;
                    const color = m.rating >= 7 ? 'bg-emerald-500' : m.rating >= 5.5 ? 'bg-amber-500' : m.rating ? 'bg-red-400' : 'bg-gray-300 dark:bg-gray-600';
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${m.opponent}: ${m.rating || 'N/A'}`}>
                        <span className="text-[9px] font-medium text-gray-500">{m.rating || '-'}</span>
                        <div className={`w-full rounded-t ${color} transition-all`} style={{ height: `${height}%` }} />
                        <span className="text-[8px] text-gray-400 truncate w-full text-center">{(m.opponent || '').substring(0, 5)}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Match detail list */}
                <div className="divide-y divide-gray-100 dark:divide-gray-700 mt-3">
                  {trend.slice().reverse().slice(0, 5).map((m, i) => (
                    <div key={i} className="flex items-center justify-between py-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">vs {m.opponent}</p>
                        <p className="text-[10px] text-gray-400">{m.match_date} &middot; {m.minutes}min &middot; {m.goals}G {m.assists}A</p>
                      </div>
                      <div className="text-right">
                        <Badge className={getRatingColor(m.rating)}>{m.rating ? `${m.rating}/10` : 'N/A'}</Badge>
                        {m.rating_label && <p className={`text-[10px] mt-0.5 ${getLabelColor(m.rating_label)}`}>{m.rating_label}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Attendance Summary */}
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="attendance-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-700 dark:text-gray-300">
                <ClipboardList size={15} className="inline mr-1.5 mb-0.5" /> Attendance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 mb-3">
                <div className="relative w-20 h-20">
                  <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke="currentColor" className="text-gray-200 dark:text-gray-700" strokeWidth="3" />
                    <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none" stroke="currentColor" className="text-pfap-green"
                      strokeWidth="3" strokeDasharray={`${att.rate || 0}, 100`} />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{att.rate || 0}%</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">{att.attended || 0}</span> of {att.total_sessions || 0} sessions attended
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {att.rate >= 85 ? 'Great attendance!' : att.rate >= 70 ? 'Good, could improve' : 'Attendance needs attention'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Flags */}
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="flags-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-700 dark:text-gray-300">
                <Flag size={15} className="inline mr-1.5 mb-0.5" /> Active Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {flags.length === 0 ? (
                <div className="text-center py-6">
                  <CheckCircle2 size={24} className="mx-auto text-emerald-500 mb-2" />
                  <p className="text-sm text-emerald-600 font-medium">No Active Alerts</p>
                  <p className="text-xs text-gray-400 mt-1">Everything looks good</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {flags.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <AlertTriangle size={14} className="text-amber-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-amber-800 dark:text-amber-300">{f.flag_type}</p>
                        <p className="text-[10px] text-amber-600 dark:text-amber-400 truncate">{f.description}</p>
                      </div>
                      <Badge className="text-[9px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex-shrink-0">{f.severity}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* PDP Progress */}
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="parent-pdp-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-700 dark:text-gray-300">
                <Target size={15} className="inline mr-1.5 mb-0.5" /> Development Plan
              </CardTitle>
              <CardDescription className="text-xs">{activeCycle ? `Active: ${activeCycle.name}` : 'No active cycle'}</CardDescription>
            </CardHeader>
            <CardContent>
              {activeCycle && cycleGoals.length > 0 ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 mb-3">
                    <Progress value={pdpProgress} className="h-2 flex-1" />
                    <span className="text-sm font-semibold text-emerald-600">{pdpProgress}%</span>
                  </div>
                  {cycleGoals.slice(0, 5).map(g => (
                    <div key={g.id} className="flex items-center justify-between py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate text-gray-900 dark:text-gray-100">{g.title}</p>
                        <Badge variant="outline" className="text-[10px] mt-0.5">{g.category}</Badge>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <Progress value={g.progress || 0} className="h-1.5 w-16" />
                        <span className="text-xs text-gray-500 w-8 text-right">{g.progress || 0}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-6">No active development plan</p>
              )}
            </CardContent>
          </Card>

          {/* Injury & Recovery */}
          <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="parent-injury-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-gray-700 dark:text-gray-300">
                <Heart size={15} className="inline mr-1.5 mb-0.5" /> Injury & Recovery
              </CardTitle>
            </CardHeader>
            <CardContent>
              {injuryData?.has_active_injury ? (
                <div className="space-y-3">
                  {injuryData.active_injuries?.map(inj => (
                    <div key={inj.id} className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-red-800 dark:text-red-400">{inj.injury_type}</span>
                        <Badge className="text-[10px] bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400">{inj.severity}</Badge>
                        <Badge className="text-[10px] bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400">{inj.status}</Badge>
                      </div>
                      {inj.recovery_progress !== undefined && (
                        <div className="flex items-center gap-2 mt-2">
                          <Progress value={inj.recovery_progress} className="h-1.5 flex-1" />
                          <span className="text-xs text-gray-500">{inj.recovery_progress}%</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <CheckCircle2 size={24} className="mx-auto text-emerald-500 mb-2" />
                  <p className="text-sm text-emerald-600 font-medium">No Active Injuries</p>
                  <p className="text-xs text-gray-400 mt-1">Athlete is fit and available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default ParentDashboard;
